import {useCallback} from 'react'
import {useShopperBasketsMutation} from '@salesforce/commerce-sdk-react'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'

/**
 * SCAPI basket custom attributes (with their `c_` response prefix) that together
 * make up one GSC creator-attribution record on a basket.
 * @type {string[]}
 */
export const GSC_ATTRIBUTION_FIELDS = [
    'c_gscAmbassadorIdEnc',
    'c_gscReferralSource',
    'c_gscAttributedAt',
    'c_gscHandoffTokenId'
]

/**
 * Picks the GSC attribution fields off a basket, dropping any field that is
 * missing or falsy. Safe to call with a `null`/`undefined` basket.
 * @param {Object|null|undefined} basket - A SCAPI basket, or any object carrying `c_gsc*` fields.
 * @returns {Object} A plain object containing only the GSC fields present on `basket`.
 */
const getAttribution = (basket) =>
    GSC_ATTRIBUTION_FIELDS.reduce((result, field) => {
        if (basket?.[field]) result[field] = basket[field]
        return result
    }, {})

/**
 * Whether an attribution record has every GSC field set to a truthy value.
 * A partial record (for example, attribution stripped mid-flight) is never "complete".
 * @param {Object|null|undefined} attribution - Attribution fields to check, typically from {@link getAttribution}.
 * @returns {boolean} True when all four GSC fields are present and truthy.
 */
const hasCompleteAttribution = (attribution) =>
    GSC_ATTRIBUTION_FIELDS.every((field) => Boolean(attribution?.[field]))

/**
 * Whether a basket already carries the exact attribution values being written,
 * used to skip a redundant `updateBasket` call on the happy path.
 * @param {Object|null|undefined} basket - The basket (or attribution-shaped object) to compare from.
 * @param {Object|null|undefined} attribution - The attribution values to compare against.
 * @returns {boolean} True when every GSC field matches between the two objects.
 */
const isSameAttribution = (basket, attribution) =>
    GSC_ATTRIBUTION_FIELDS.every((field) => basket?.[field] === attribution?.[field])

/**
 * Decides whether a basket lifecycle event (login merge, ESW rebuild, saved-basket
 * restore) should keep the destination basket's existing attribution instead of
 * overwriting it with the source basket's attribution.
 *
 * This guards the multi-touch case (TSD scenario: creator A, then creator B within
 * 30 days): the more recently attributed basket wins, never the one processed last.
 * An unparsable `c_gscAttributedAt` on either side degrades to "the other side wins"
 * rather than throwing, since a malformed timestamp must never block a restamp.
 *
 * @param {Object|null|undefined} source - Attribution fields to be written, typically from {@link getAttribution}.
 * @param {Object|null|undefined} target - The destination basket to compare against.
 * @returns {boolean} True when `target`'s own attribution is complete, different from `source`, and strictly newer.
 */
const shouldKeepTargetAttribution = (source, target) => {
    const targetAttribution = getAttribution(target)
    if (!hasCompleteAttribution(targetAttribution) || isSameAttribution(target, source)) return false

    const sourceTime = Date.parse(source?.c_gscAttributedAt)
    const targetTime = Date.parse(targetAttribution.c_gscAttributedAt)
    return Number.isFinite(targetTime) && (!Number.isFinite(sourceTime) || targetTime > sourceTime)
}

/**
 * Parses a basket's raw `c_gscFailedItems` JSON string into an array. Malformed or
 * missing JSON degrades to an empty array rather than throwing, since a partially-
 * readable diagnostic record (or none at all) must never block a basket write.
 * @param {string|null|undefined} rawValue - The raw `basket.c_gscFailedItems` value.
 * @returns {Array<{sku: string, qty: number, reason: string}>}
 */
const parseFailedItemsField = (rawValue) => {
    try {
        const parsed = rawValue ? JSON.parse(rawValue) : []
        return Array.isArray(parsed) ? parsed : []
    } catch (error) {
        return []
    }
}

/**
 * Merges a new batch of failed items into whatever failed-items history a basket
 * already carries, so one batch's failures never erase an earlier batch's failures
 * for a different SKU. Business decision (GSC-547): the note is a record of what
 * happened on each creator link, not just the most recent one, so it must never be
 * silently overwritten — this applies equally to a second handoff on the same basket
 * (see {@link useGscAttribution}'s `stampBasket`) and to a basket-lifecycle event that
 * carries one basket's failures onto another (see `restampBasket`).
 *
 * When the same SKU appears in both, the new occurrence's reason replaces the old one
 * for that SKU (the most recent attempt is the more useful explanation) rather than
 * keeping both. Relative order is: carried-forward entries first, then the new ones.
 *
 * @param {Object|null|undefined} basket - The basket to read existing `c_gscFailedItems` from.
 * @param {Array<{sku: string, qty: number, reason: string}>} newFailedItems - The
 *   failures to merge in, already validated non-empty by the caller.
 * @returns {Array<{sku: string, qty: number, reason: string}>} The merged array to persist.
 */
const mergeFailedItems = (basket, newFailedItems) => {
    const existing = parseFailedItemsField(basket?.c_gscFailedItems)
    const carriedForward = existing.filter(
        (item) => !newFailedItems.some((newItem) => newItem.sku === item?.sku)
    )
    return carriedForward.concat(newFailedItems)
}

/**
 * Logs a GSC basket-mutation failure under the shared `gsc` namespace.
 * Never throws, so it is always safe to call from a `catch` block.
 * @param {string} operation - Short label for the failed operation, e.g. `'basket attribution stamp'`.
 * @param {Error|null|undefined} error - The error that was caught.
 * @returns {void}
 */
const logFailure = (operation, error) => {
    logger.error(`GSC ${operation} failed`, {
        namespace: 'gsc',
        additionalProperties: {error: error?.message, stack: error?.stack}
    })
}

/**
 * Provides the two basket-mutation helpers that write GSC creator attribution:
 * `stampBasket` for a fresh handoff resolve, and `restampBasket` for carrying an
 * existing attribution across a basket lifecycle event (login merge, ESW currency/
 * country switch, saved-basket restore). Both are no-ops (no network call) when
 * their inputs are incomplete, so callers can invoke them unconditionally.
 * @returns {{stampBasket: Function, restampBasket: Function}}
 */
const useGscAttribution = () => {
    const {data: currentBasket} = useCurrentBasket()
    const {mutateAsync: updateBasket} = useShopperBasketsMutation('updateBasket')

    /**
     * Writes a freshly resolved GSC attribution onto a basket via a standard
     * `updateBasket` call. Skips the network call when the basket already carries
     * the same values, or when the attribution payload is incomplete (a GSC
     * resolve response is only usable once it has an ambassador id, a referral
     * source, and a token id).
     *
     * @param {string|null|undefined} basketId - The basket to stamp.
     * @param {{ambassadorIdEnc?: string, referralSource?: string, attributedAt?: string}|null|undefined} attribution
     *   - The `c_attribution` object from a resolved GSC handoff response.
     * @param {string|null|undefined} tokenId - The `gscToken` that produced this attribution.
     * @param {Object|null|undefined} [targetBasket] - The already-fetched basket to compare
     *   against and to fall back to on failure, avoiding a redundant `useCurrentBasket` read.
     * @param {Array<{sku: string, qty: number, reason: string}>} [failedItems] - Items from this
     *   handoff that could not be added to the basket, if any. Written alongside the attribution
     *   so `orderHooks.js` can turn it into an order note at checkout. Passing a non-empty array
     *   forces the write even when the attribution fields themselves are unchanged, since there's
     *   new diagnostic information to persist either way.
     * @returns {Promise<Object|null>} The updated basket on success, the unchanged
     *   comparison basket when nothing needed to change or the write failed, or `null`
     *   when there was no basket to fall back to. Never rejects.
     */
    const stampBasket = useCallback(
        async (basketId, attribution, tokenId, targetBasket, failedItems) => {
            if (!basketId || !attribution?.ambassadorIdEnc || !attribution?.referralSource || !tokenId) {
                return targetBasket || null
            }

            const body = {
                c_gscAmbassadorIdEnc: attribution.ambassadorIdEnc,
                c_gscReferralSource: attribution.referralSource,
                c_gscAttributedAt: attribution.attributedAt || new Date().toISOString(),
                c_gscHandoffTokenId: tokenId
            }
            const comparisonBasket = targetBasket || (currentBasket?.basketId === basketId ? currentBasket : null)
            const hasFailedItems = Array.isArray(failedItems) && failedItems.length > 0
            if (hasFailedItems) {
                body.c_gscFailedItems = JSON.stringify(mergeFailedItems(comparisonBasket, failedItems))
            }
            if (!hasFailedItems && isSameAttribution(comparisonBasket, body)) return comparisonBasket

            try {
                return await updateBasket({parameters: {basketId}, body})
            } catch (error) {
                logFailure('basket attribution stamp', error)
                return comparisonBasket
            }
        },
        [currentBasket, updateBasket]
    )

    /**
     * Copies an existing GSC attribution from `sourceBasket` onto `targetBasketId`,
     * used whenever a basket lifecycle event replaces or rebuilds the basket that
     * was originally stamped (guest-to-registered login merge, SSO merge, ESW
     * currency/country switch, saved-basket restore). No-ops when the source has
     * no complete attribution to copy, or when the target basket already has a
     * complete attribution that is the same or newer than the source's — see
     * {@link shouldKeepTargetAttribution}.
     *
     * `sourceBasket`'s `c_gscFailedItems`, if any, is carried across too (merged into
     * whatever the target already carries, via {@link mergeFailedItems}), and on its
     * own forces the write even when attribution would otherwise be left alone —
     * without this, a just-recorded failed-items note is silently dropped the moment
     * a basket gets rebuilt or merged, which defeats the point of recording it at all.
     *
     * @param {Object|null|undefined} sourceBasket - The basket to copy attribution (and
     *   any `c_gscFailedItems`) from.
     * @param {string|null|undefined} targetBasketId - The basket to write attribution onto.
     * @param {Object|null|undefined} [targetBasket] - The already-fetched target basket to
     *   compare against and to fall back to on failure, avoiding a redundant read.
     * @returns {Promise<Object|null>} The updated basket on success, the unchanged
     *   comparison basket when nothing needed to change or the write failed, or `null`
     *   when there was no basket to fall back to. Never rejects.
     */
    const restampBasket = useCallback(
        async (sourceBasket, targetBasketId, targetBasket) => {
            const attribution = getAttribution(sourceBasket)
            if (!targetBasketId || !hasCompleteAttribution(attribution)) return targetBasket || null

            const comparisonBasket =
                targetBasket || (currentBasket?.basketId === targetBasketId ? currentBasket : null)

            const keepTargetAttribution = shouldKeepTargetAttribution(attribution, comparisonBasket)
            const sourceFailedItems = parseFailedItemsField(sourceBasket?.c_gscFailedItems)
            const hasFailedItems = sourceFailedItems.length > 0

            if (!hasFailedItems && (isSameAttribution(comparisonBasket, attribution) || keepTargetAttribution)) {
                return comparisonBasket
            }

            // When target's own attribution should win, write it back unchanged and only
            // carry the failed-items history across - a failed-items carry-forward must
            // never revert a newer, already-correct target attribution to the source's
            // older one.
            const body = keepTargetAttribution ? getAttribution(comparisonBasket) : {...attribution}
            if (hasFailedItems) {
                body.c_gscFailedItems = JSON.stringify(mergeFailedItems(comparisonBasket, sourceFailedItems))
            }

            try {
                return await updateBasket({parameters: {basketId: targetBasketId}, body})
            } catch (error) {
                logFailure('basket attribution restamp', error)
                return comparisonBasket
            }
        },
        [currentBasket, updateBasket]
    )

    return {stampBasket, restampBasket}
}

export {getAttribution, hasCompleteAttribution, isSameAttribution, shouldKeepTargetAttribution}
export default useGscAttribution
